import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Briefcase,
  Settings as SettingsIcon,
  LogOut,
  Mail,
  MessageCircle,
  Bot,
  CalendarClock,
  FolderKanban,
  TrendingUp,
  Inbox,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/NotificationsBell";
import AIAssistantWidget from "@/components/AIAssistantWidget";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testId: "nav-dashboard", screen: "dashboard", action: "view" },
  { to: "/vagas", label: "Vagas", icon: Briefcase, testId: "nav-vagas", screen: "jobs", action: "view" },
  { to: "/cadastros", label: "Cadastros", icon: FolderKanban, testId: "nav-cadastros", screen: "registry", action: "organizations" },
  { to: "/comunicacoes/emails", label: "E-mails", icon: Mail, testId: "nav-emails", screen: "emails", action: "view" },
  { to: "/comunicacoes/whatsapp", label: "WhatsApp", icon: MessageCircle, testId: "nav-whatsapp", screen: "whatsapp", action: "view" },
  { to: "/comunicacoes/fluxos", label: "Fluxos IA", icon: Bot, testId: "nav-fluxos", screen: "settings", action: "whatsapp" },
  { to: "/minha-agenda", label: "Minha agenda", icon: CalendarClock, testId: "nav-agenda" },
  { to: "/relatorios", label: "Relatórios", icon: TrendingUp, testId: "nav-relatorios", screen: "dashboard", action: "view" },
  { to: "/comunicacoes/linkedin", label: "LinkedIn", icon: Inbox, testId: "nav-inmails", screen: "jobs", action: "view" },
  { to: "/configuracoes", label: "Configurações", icon: SettingsIcon, testId: "nav-config", screen: "settings", action: "company" },
];


const itemClass = ({ isActive }) =>
  `group relative flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all ${
    isActive
      ? "bg-blue-600/20 text-white border border-blue-500/20"
      : "text-white/60 hover:text-white hover:bg-white/8"
  }`;

const AppLayout = () => {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();

  const visibleNav = navItems.filter((item) =>
    !item.screen ||
    can(item.screen, item.action) ||
    (item.screen === "settings" && (can("access", "profiles") || can("access", "users"))) ||
    (item.screen === "registry" && can("registry", "departments"))
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const initials = (user?.name || "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen flex" style={{ background: "#f0f4f8" }}>
      {/* ── Sidebar ───────────────────────────────────────── */}
      <aside
        data-testid="app-sidebar"
        style={{
          width: 240,
          position: "fixed",
          left: 0,
          top: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(175deg, #0d1726 0%, #0B1220 45%, #0d1f38 100%)",
          borderRight: "1px solid rgba(255,255,255,0.06)",
          boxShadow: "4px 0 24px rgba(0,0,0,0.35)",
          zIndex: 50,
        }}
      >
        {/* Logo row */}
        <div style={{ padding: "20px 16px 14px", display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src="/logo-gr-horizontal.png"
            alt="GridRecruiter"
            style={{ height: 40, width: "auto", display: "block", flex: 1, minWidth: 0, objectFit: "contain", objectPosition: "left center" }}
            draggable={false}
          />
          <NotificationsBell />
        </div>

        {/* Thin divider */}
        <div style={{ height: 1, background: "rgba(255,255,255,0.07)", margin: "0 14px 8px" }} />

        {/* Nav */}
        <nav style={{ flex: 1, padding: "4px 10px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
          {visibleNav.map(({ to, label, icon: Icon, end, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testId}
              className={itemClass}
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`w-[17px] h-[17px] ${isActive ? "text-[#00D4CB]" : "text-white/50 group-hover:text-white/80"}`}
                    strokeWidth={2}
                  />
                  <span className="flex-1">{label}</span>
                  {isActive && (
                    <span
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "#00D4CB",
                        boxShadow: "0 0 10px rgba(0,212,203,0.9)",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User block */}
        <div
          style={{
            padding: "12px 14px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #2563EB 0%, #00D4CB 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 700,
                color: "#fff",
                flexShrink: 0,
                boxShadow: "0 2px 10px rgba(37,99,235,0.4)",
              }}
            >
              {initials || "RH"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                data-testid="sidebar-user-name"
                style={{ fontSize: 13, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {user?.name}
              </div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {user?.email}
              </div>
            </div>
          </div>
        </div>

        {/* Logout */}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          style={{
            margin: "4px 10px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 14px",
            borderRadius: 10,
            border: "none",
            background: "transparent",
            color: "rgba(255,255,255,0.5)",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
            transition: "color 0.15s, background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
        >
          <LogOut style={{ width: 15, height: 15 }} />
          Sair
        </button>
      </aside>

      {/* ── Main content ─────────────────────────────────── */}
      <main style={{ marginLeft: 240, flex: 1, minHeight: "100vh" }}>
        <div style={{ padding: "36px 40px", maxWidth: 1400, margin: "0 auto" }}>
          <Outlet />
        </div>
      </main>

      {/* Maya — AI assistant floating widget */}
      <AIAssistantWidget />
    </div>
  );
};

export default AppLayout;
